import { ClassKey } from "@const/class.constants";
import { HwTypesResponseDTO, IHwTypeBase } from "@dto/architecture.dto";
import { GroupCustomGroupFolder, GroupCustomGroupFoldersDTO } from "@dto/customGroup.dto";
import { GroupDashboardFolder, GroupDashboardFoldersDTO } from "@dto/dashboard.dto";
import { MenuItem, MenuLink, MenuResponseDTO } from "@dto/menuResponse.dto";
import { GroupGroupFolder, GroupGroupsResponseDTO } from "@dto/storageGroups.dto";
import axios, { AxiosError } from "axios";
import { useAppGlobStore } from "src/appGlobalStore";
import { API_URL } from "../data/Api";
import { isClassTypeNav } from "../data/Class";
import { Label } from "../data/Label";
import { RoutePath } from "../data/Routes";
import { MenuNode } from '../model/MenuNode';
import { Deferred } from "../util/Deferred";
import { GLOB } from "../util/Glob";
import { Log } from './log.service';

const PATH_PAGE = '/page';
const PATH_SEP = '/';
const KEY_GD = 'gd';
const KEY_GCG = 'gcg';
const KEY_GSG = 'gsg';


export class MenuService {

  readonly hwTypesLoaded = new Deferred<IHwTypeBase[]>();
  readonly menuFiltered = new Deferred<IHwTypeBase[]>();

  /**
   * Storages by device, type and group
   */
  readonly storagesBy: Record<'device' | 'type' | 'group', MenuNode> = {
    device: { key: RoutePath.DEVICE, keyClass: ClassKey.STORAGE, title: 'Storage devices', path: RoutePath.DEVICE, children: [] },
    type: { key: RoutePath.TYPE, keyClass: ClassKey.STORAGE, title: 'Storage types', path: RoutePath.TYPE, children: [] },
    group: { key: RoutePath.GROUP, keyClass: ClassKey.STORAGE, title: 'Storage groups', path: RoutePath.GROUP, children: [] }
  };

  private _hwTypes: IHwTypeBase[] = [];
  private _deviceClasses: MenuItem[] = [];

  get activeClasses() {
    return this._deviceClasses.filter(mi => mi.isActive);
  }

  get allClasses() {
    return [...this._deviceClasses];
  }

  get hwTypes() { return this._hwTypes; }

  getRouteLink(url: string, tab?: string, subTab?: string, relative = true, deviceName?: string) {
    if (subTab && url.startsWith(API_URL.CUSTOM_GROUP_MENU + PATH_SEP)) {
      tab = subTab;
      subTab = undefined;
    }

    if (tab && !url.includes('tab=')) {
      tab = '#tab=' + encodeURIComponent(tab);
      if (subTab)
        subTab = '&subTab=' + encodeURIComponent(subTab);
      else
        subTab = '';
      tab += subTab;
    }
    else {
      tab = '';
    }

    if (url.startsWith(API_URL.CUSTOM_GROUP_MENU + PATH_SEP)) {
      const idx = url.lastIndexOf('/');
      const id = url.substring(idx);
      return RoutePath.CUSTOM_GROUPS + id + tab;
    }
    if (url.startsWith(API_URL.MENU + PATH_PAGE + RoutePath.STORAGES + PATH_SEP)) {
      const idx = url.lastIndexOf('/');
      const id = url.substring(idx);
      return RoutePath.STORAGE + RoutePath.DEVICE + id + tab;
    }
    if (url.startsWith(API_URL.MENU + PATH_PAGE + RoutePath.SAN + PATH_SEP)) {
      const idx = url.lastIndexOf('/');
      const id = url.substring(idx);
      return RoutePath.SAN + RoutePath.TYPE + id + tab;
    }
    if (url.startsWith(API_URL.MENU + PATH_PAGE + RoutePath.LAN_CFG + PATH_SEP)) {
      const idx = url.lastIndexOf('/');
      const id = url.substring(idx);
      return RoutePath.LAN_CFG + RoutePath.TYPE + id + tab;
    }
    if (url.startsWith(API_URL.MENU + PATH_PAGE + RoutePath.TAPE + PATH_SEP)) {
      const idx = url.lastIndexOf('/');
      const id = url.substring(idx);
      return RoutePath.TAPE + RoutePath.TYPE + id + tab;
    }
    if (url.startsWith(API_URL.MENU + PATH_PAGE + RoutePath.SGROUP + PATH_SEP)) {
      const parsed = new URL('http://some' + url);
      const groupId = parsed.searchParams.get('item_id');
      return RoutePath.STORAGE + RoutePath.GROUP + '/' + groupId + url.substring(url.lastIndexOf('/'));
    }
    const nav = url.replace(MenuLink.API_URL_PAGE, '');
    const sep = nav.indexOf('/');
    const type = nav.substring(0, sep);
    const item = nav.substring(sep);
    for (const ht of this._hwTypes) {
      if (ht.hw_type === type) {
        let allType = '', group = '';
        if (isClassTypeNav(ht.class))
          allType = RoutePath.TYPE;
        else if (relative && '/' + ht.class === GLOB.selectedClass) {
          allType = GLOB.selectedAllType;
          group = GLOB.selectedGroupId;
        } else if (ht.class === ClassKey.STORAGE) {
          if (deviceName) {
            return `/${ht.class}${RoutePath.DEVICE}/${deviceName}${item}${tab}`;
          }
          allType = RoutePath.TYPE;
        }
        // else if (isClassSTOR(ht.class)) {
        //   allType = GLOB.selectedAllType || RoutePath.DEVICE;
        // }
        return `/${ht.class}${allType || ''}${group ? '/' + group : ''}/${type}${item}${tab}`;
      }
    }
    if ('/' + type === RoutePath.STORAGE) {
      return `/${type}${RoutePath.DEVICE}${item}${tab}`;
    }

    return (nav?.startsWith('/') ? '' : '/') + nav + tab;
  }

  getDashMenu() {
    return useAppGlobStore.getState().topMenu.find(mn => mn.key === RoutePath.DASHBOARD);
  }

  init() {
    this.hwTypesLoaded.reset();
    axios.get<HwTypesResponseDTO>(API_URL.CONFIGURATION + '/hw_types').then(response => {
      this._hwTypes = response.data.data;
      this.hwTypesLoaded.resolve(this._hwTypes);
    }, (reason: AxiosError) => {
      Log.error('Failed to get HW types!', reason);
      this.hwTypesLoaded.reject(reason);
    });

    const topMenu: MenuNode[] = [];
    const menuRequests = [
      axios.get<MenuResponseDTO>(API_URL.DASHBOARD_MENU),
      axios.get<GroupDashboardFoldersDTO>(API_URL.DASHBOARD_MENU_GROUP),
      axios.get<MenuResponseDTO>(API_URL.CUSTOM_GROUP_MENU),
      axios.get<GroupCustomGroupFoldersDTO>(API_URL.CUSTOM_GROUP_MENU_GROUP),
      axios.get<MenuResponseDTO>(MenuItem.API_URL_GEN),
      axios.get<MenuResponseDTO>(API_URL.STORAGE_GROUPS),
      axios.get<GroupGroupsResponseDTO>(API_URL.STORAGE_GROUP_GROUPS),
      axios.get<MenuResponseDTO>(API_URL.MENU + RoutePath.FOLDER + RoutePath.CLASS + RoutePath.STORAGE),
      axios.get<MenuResponseDTO>(API_URL.MENU + RoutePath.TOOLS + RoutePath.TOPOLOGY),
      axios.get<MenuResponseDTO>(API_URL.MENU + RoutePath.TOOLS + RoutePath.TRENDS)
    ] as const;

    topMenu.push({ key: RoutePath.DASHBOARD, title: Label.DASHBOARD, path: RoutePath.DASHBOARD });

    topMenu.push({ key: RoutePath.CUSTOM_GROUPS, title: Label.CUSTOM_GROUPS, path: RoutePath.CUSTOM_GROUPS });

    return Promise.all(menuRequests).then(responses => {

      const dashMenu = this.createGroupMenu(responses[0].data.data, responses[1].data.data, KEY_GD);
      this.positionDashboardMenu(dashMenu);
      topMenu[0].children = dashMenu;

      topMenu[1].children = this.createGroupMenu(responses[2].data.data, responses[3].data.data, KEY_GCG, false, true);

      this._deviceClasses = responses[4].data.data;
      const isSomeActive = this._deviceClasses.some(c => c.isActive);
      for (const cls of this._deviceClasses) {
        if (isSomeActive && !cls.isActive) continue;
        topMenu.push({ key: cls.class, title: cls.title, path: '/' + cls.class, url: '/' + cls.class, notActive: !cls.isActive, children: cls.children?.map(MenuService.menuItemToMenuNode) });
      }

      const topStorage = topMenu.find(mn => mn.key === ClassKey.STORAGE);
      if (topStorage) {
        this.storagesBy.type.children = topStorage.children;
        this.storagesBy.device.children = responses[7].data.data.map((mi: MenuItem) => MenuService.menuItemToMenuNode(mi));
        this.storagesBy.group.children = this.createGroupMenu(responses[5].data.data, responses[6].data.data, KEY_GSG, true);
      }

      const topologyMenu = responses[8].data.data.map((mi: MenuItem) => MenuService.menuItemToMenuNode(mi));
      const trendsMenu = responses[9].data.data.map((mi: MenuItem) => MenuService.menuItemToMenuNode(mi));
      useAppGlobStore.setState({ topMenu, topologyMenu, trendsMenu });

    }, (reason: string) => {
      Log.error('Failed to get class sub-menus!', reason);
      return Promise.reject(new Error(reason));
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  private createGroupMenu(personal: MenuItem[], group: (GroupDashboardFolder | GroupCustomGroupFolder | GroupGroupFolder)[], keyPrefix: string, isStorageGroups = false, foldersLast = false): MenuNode[] {
    const menu = personal.map(mi => MenuService.menuItemToMenuNode(mi)).sort(MenuService.getFolderSorter(foldersLast));
    if (group?.length && !group.find(g => g.isGroupFolder)) menu.push(MenuNode.getDivider(MenuNode.GROUPS_DIVIDER_KEY, false));
    menu.push(...group.map(mi => {
      const mn = MenuService.menuItemToMenuNode(mi);
      if (isStorageGroups) {
        mn.isGroupFolder = true;
        mn.isGroupedGroupFolder = true;
      }
      mn.children?.sort(MenuService.getFolderSorter(foldersLast));
      return ({ ...mn, key: keyPrefix + mi.group_id, itemId: mi.group_id + '' });
    }).sort(MenuService.getFolderSorter()));

    return menu;
  }

  private positionDashboardMenu(nodes: MenuNode[]) {

    nodes.unshift(MenuNode.getDivider('dash-overview', false));

    for (const excludeID of MenuNode.DASH_EXCLUDED_ITEM_IDS) {
      const hmIdx = nodes.findIndex(mn => mn.itemId === excludeID);
      if (hmIdx > 0) {
        nodes.unshift(...nodes.splice(hmIdx, 1));
      }
    }
  }

  private static menuItemToMenuNode(mi: MenuItem): MenuNode {
    return {
      ...MenuNode.menuItemToNode(mi), path: mi.isLeaf ? null : GLOB.getItemPath(mi)
    };
  }

  static getFolderSorter(foldersLast = false, pageType?: string) {
    const folderOrder = foldersLast ? -1 : 1;
    return (a: MenuNode, b: MenuNode) => {
      if (pageType && (a.pageType && !a.pageType.includes(pageType) || b.pageType && !b.pageType.includes(pageType))) return 0;
      if (a.isLeaf) {
        if (b.isLeaf) {
          // keep folders first; among leaves the default page (folder Totals) leads
          if (!!a.isDefault !== !!b.isDefault) return a.isDefault ? -1 : 1;
          return GLOB.naturalSort(a.title, b.title);
        }
        return folderOrder;
      }
      if (b.isLeaf) return -folderOrder;
      return GLOB.naturalSort(a.title, b.title);
    };
  }

  reloadCustomGroups() {
    return Promise.all([axios.get<MenuResponseDTO>(API_URL.CUSTOM_GROUP_MENU),
    axios.get<GroupCustomGroupFoldersDTO>(API_URL.CUSTOM_GROUP_MENU_GROUP)]).then(responses => {
      const menu = useAppGlobStore.getState().topMenu;
      menu[1].children = this.createGroupMenu(responses[0].data.data, responses[1].data.data, KEY_GCG, false, true);
      useAppGlobStore.setState({ topMenu: [...menu] });
    }, (reason: AxiosError) => Log.error('Failed to refresh custom groups menu!', reason));
  }

  reloadDashboards() {
    return Promise.all([axios.get<MenuResponseDTO>(API_URL.DASHBOARD_MENU),
    axios.get<GroupDashboardFoldersDTO>(API_URL.DASHBOARD_MENU_GROUP)]).then(responses => {
      const groupMenu = this.createGroupMenu(responses[0].data.data, responses[1].data.data, KEY_GD);
      this.positionDashboardMenu(groupMenu);
      const menu = useAppGlobStore.getState().topMenu;
      menu[0].children = groupMenu;
      useAppGlobStore.setState({ topMenu: [...menu] });
    }, (reason: AxiosError) => Log.error('Failed to refresh dashboards!', reason));
  }

  reloadStorageGroups(): Promise<void> {
    return Promise.all([axios.get<MenuResponseDTO>(API_URL.STORAGE_GROUPS),
    axios.get<GroupGroupsResponseDTO>(API_URL.STORAGE_GROUP_GROUPS)]).then(responses => {
      this.storagesBy.group.children = this.createGroupMenu(responses[0].data.data, responses[1].data.data, KEY_GSG);
    }, (reason: AxiosError) => Log.error('Failed to refresh storage groups menu!', reason));
  }

  /**
   * Get menu with content (children) only
   * @returns filtered menu
   */
  static getActiveMenu() {
    return useAppGlobStore.getState().topMenu?.filter(mn => mn.children?.length);
  }
}
